import {
  copiesOwned,
  loadCollection,
  MAX_COPIES,
  ownedVerseIds,
  STARTER_OWNED_IDS,
} from "../collection";
import {
  catalogById,
  DECK_MAX,
  DEFAULT_DECK_IDS,
  effectiveDeckMax,
  effectiveDeckMin,
  resolveVerseId,
} from "./catalog";

/** Legacy single-deck key (migrated on first load). */
const LEGACY_KEY = "sots-deck-v2:proclaim";
const LEGACY_V1_KEY = "sots-player-deck-v1";
/** Multi-deck library. */
const LIBRARY_KEY = "sots-decks-v3";

export interface NamedDeck {
  id: string;
  name: string;
  /**
   * Ordered loadout. Duplicate ids are allowed (multiple copies of a verse),
   * capped per-type by copies owned and overall by deck max.
   */
  verseIds: string[];
  updatedAt: number;
}

export interface DeckLibrary {
  activeId: string;
  decks: NamedDeck[];
}

/** @deprecated Prefer NamedDeck — kept for DeckPicker onChange compatibility. */
export type SavedDeck = NamedDeck;

const DEFAULT_NAME = "My loadout";

function uniqueOwnedCount(): number {
  return ownedVerseIds().length;
}

function deckMin(): number {
  return effectiveDeckMin(uniqueOwnedCount());
}

function deckMax(): number {
  return effectiveDeckMax(uniqueOwnedCount());
}

/**
 * Sanitize deck list to owned catalog verses only.
 * Duplicate ids are kept up to copies owned (collection multiplicity).
 * Invalid / unowned ids are dropped; total length is capped at deck max.
 */
function sanitizeDeck(ids: string[]): string[] {
  const out: string[] = [];
  const used: Record<string, number> = {};
  const max = deckMax();
  for (const raw of ids) {
    const id = resolveVerseId(raw);
    const owned = copiesOwned(id);
    if (owned < 1) continue;
    const n = used[id] ?? 0;
    if (n >= owned) continue;
    used[id] = n + 1;
    out.push(id);
    if (out.length >= max) break;
  }
  return out;
}

/** Top up a short deck with owned starter / other owned verses (multiplicity-aware). */
function fillToMin(ids: string[]): string[] {
  const min = deckMin();
  const max = deckMax();
  const out = [...ids];
  const used: Record<string, number> = {};
  for (const id of out) used[id] = (used[id] ?? 0) + 1;

  const tryAdd = (id: string, onlyIfMissing = false): boolean => {
    if (out.length >= min) return false;
    const owned = copiesOwned(id);
    const n = used[id] ?? 0;
    if (owned < 1 || n >= owned) return false;
    if (onlyIfMissing && n >= 1) return false;
    out.push(id);
    used[id] = n + 1;
    return true;
  };

  // 1) Prefer unique types first (starters, then rest) so fill diversifies before stacking
  for (const id of STARTER_OWNED_IDS) {
    if (out.length >= min) break;
    tryAdd(id, true);
  }
  for (const id of ownedVerseIds()) {
    if (out.length >= min) break;
    tryAdd(id, true);
  }
  // 2) Then stack extra owned copies if still under min
  let guard = 0;
  while (out.length < min && guard++ < 200) {
    let added = false;
    for (const id of STARTER_OWNED_IDS) {
      if (tryAdd(id)) added = true;
      if (out.length >= min) break;
    }
    if (out.length >= min) break;
    for (const id of ownedVerseIds()) {
      if (tryAdd(id)) added = true;
      if (out.length >= min) break;
    }
    if (!added) break;
  }
  return out.slice(0, max);
}

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `deck-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function uniqueName(base: string, existing: NamedDeck[], exceptId?: string): string {
  const names = new Set(
    existing.filter((d) => d.id !== exceptId).map((d) => d.name.toLowerCase()),
  );
  let name = base.trim() || DEFAULT_NAME;
  if (!names.has(name.toLowerCase())) return name;
  let n = 2;
  while (names.has(`${name} ${n}`.toLowerCase())) n++;
  return `${name} ${n}`;
}

/**
 * Persistable ids: catalog verses only (aliases remapped). Does **not**
 * drop unowned cards or pad to min — those mutations belong on save / play.
 */
function persistableIds(ids: string[]): string[] {
  const out: string[] = [];
  const used: Record<string, number> = {};
  for (const raw of ids) {
    const id = resolveVerseId(raw);
    if (!catalogById(id)) continue;
    const n = used[id] ?? 0;
    if (n >= MAX_COPIES) continue;
    used[id] = n + 1;
    out.push(id);
    if (out.length >= DECK_MAX) break;
  }
  return out;
}

/** Raw library as stored. Never fill or drop unowned verses. */
function parseStoredLibrary(): DeckLibrary | null {
  try {
    const raw = localStorage.getItem(LIBRARY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DeckLibrary;
    if (!parsed?.decks?.length) return null;
    const decks = parsed.decks.map((d) => ({
      id: String(d.id || newId()),
      name: String(d.name || DEFAULT_NAME).slice(0, 40),
      verseIds: persistableIds(d.verseIds ?? []),
      updatedAt: Number(d.updatedAt) || 0,
    }));
    if (!decks.length) return null;
    const activeId = decks.some((d) => d.id === parsed.activeId)
      ? parsed.activeId
      : decks[0]!.id;
    return { activeId, decks };
  } catch {
    return null;
  }
}

/** Ensure a library exists (migrate / starter) and return the stored form. */
function ensureStoredLibrary(): DeckLibrary {
  const stored = parseStoredLibrary();
  if (stored) return stored;
  // Side effect: migrate or write starter via readLibrary
  readLibrary();
  return parseStoredLibrary() ?? {
    activeId: "",
    decks: [starterDeck()],
  };
}

function starterDeck(name = DEFAULT_NAME): NamedDeck {
  // Ensure collection is initialized
  loadCollection();
  return {
    id: newId(),
    name,
    verseIds: fillToMin(sanitizeDeck([...DEFAULT_DECK_IDS])),
    updatedAt: 0,
  };
}

function migrateFromLegacy(): DeckLibrary | null {
  const min = deckMin();
  // v2 single deck
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { verseIds?: string[]; updatedAt?: number };
      const verseIds = fillToMin(sanitizeDeck(parsed.verseIds ?? []));
      if (verseIds.length >= min) {
        const deck: NamedDeck = {
          id: newId(),
          name: DEFAULT_NAME,
          verseIds,
          updatedAt: parsed.updatedAt ?? Date.now(),
        };
        return { activeId: deck.id, decks: [deck] };
      }
    }
  } catch {
    /* ignore */
  }
  // v1
  try {
    const legacy = localStorage.getItem(LEGACY_V1_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy) as { verseIds?: string[] };
      const verseIds = fillToMin(sanitizeDeck(parsed.verseIds ?? []));
      if (verseIds.length >= min) {
        const deck: NamedDeck = {
          id: newId(),
          name: DEFAULT_NAME,
          verseIds,
          updatedAt: Date.now(),
        };
        return { activeId: deck.id, decks: [deck] };
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

function readLibrary(): DeckLibrary {
  loadCollection();
  const stored = parseStoredLibrary();
  if (stored) {
    // View: owned-only for play/UI. Unowned ids stay on disk until the
    // player saves — never pad with starters on read.
    return {
      activeId: stored.activeId,
      decks: stored.decks.map((d) => ({
        ...d,
        verseIds: sanitizeDeck(d.verseIds),
      })),
    };
  }

  const migrated = migrateFromLegacy();
  if (migrated) {
    writeLibrary(migrated);
    try {
      localStorage.removeItem(LEGACY_KEY);
    } catch {
      /* ignore */
    }
    return migrated;
  }

  const deck = starterDeck();
  const lib: DeckLibrary = { activeId: deck.id, decks: [deck] };
  writeLibrary(lib);
  return lib;
}

function writeLibrary(lib: DeckLibrary): void {
  localStorage.setItem(LIBRARY_KEY, JSON.stringify(lib));
  // Mirror active deck to legacy key so any old readers still work
  const active = lib.decks.find((d) => d.id === lib.activeId) ?? lib.decks[0];
  if (active) {
    try {
      localStorage.setItem(
        LEGACY_KEY,
        JSON.stringify({
          verseIds: active.verseIds,
          updatedAt: active.updatedAt,
        }),
      );
    } catch {
      /* ignore */
    }
  }
}

export function listDecks(): NamedDeck[] {
  return readLibrary().decks.map((d) => ({
    ...d,
    verseIds: [...d.verseIds],
  }));
}

export function getActiveDeckId(): string {
  return readLibrary().activeId;
}

export function getDeck(id: string): NamedDeck | null {
  const d = readLibrary().decks.find((x) => x.id === id);
  return d ? { ...d, verseIds: [...d.verseIds] } : null;
}

/** Active loadout (current default for free play / resolve). */
export function loadDeck(id?: string | null): NamedDeck {
  const lib = readLibrary();
  const want = id ?? lib.activeId;
  const hit = lib.decks.find((d) => d.id === want) ?? lib.decks[0];
  if (hit) return { ...hit, verseIds: [...hit.verseIds] };
  const fresh = starterDeck();
  writeLibrary({ activeId: fresh.id, decks: [fresh] });
  return { ...fresh, verseIds: [...fresh.verseIds] };
}

export function setActiveDeck(id: string): NamedDeck | null {
  const lib = ensureStoredLibrary();
  if (!lib.decks.some((d) => d.id === id)) return null;
  if (lib.activeId !== id) {
    lib.activeId = id;
    writeLibrary(lib);
  }
  return loadDeck(id);
}

/**
 * Save verse list onto an existing deck (default: active).
 * Updates active pointer to that deck.
 */
export function saveDeck(verseIds: string[], id?: string | null): NamedDeck {
  loadCollection();
  const cleaned = sanitizeDeck(verseIds);
  const min = deckMin();
  const max = deckMax();
  if (cleaned.length < min) {
    throw new Error(`Deck needs at least ${min} verses.`);
  }
  if (cleaned.length > max) {
    throw new Error(`Deck may have at most ${max} verses.`);
  }
  const lib = ensureStoredLibrary();
  const targetId = id ?? lib.activeId;
  const idx = lib.decks.findIndex((d) => d.id === targetId);
  if (idx < 0) {
    const deck: NamedDeck = {
      id: newId(),
      name: uniqueName(DEFAULT_NAME, lib.decks),
      verseIds: cleaned,
      updatedAt: Date.now(),
    };
    lib.decks.push(deck);
    lib.activeId = deck.id;
    writeLibrary(lib);
    return { ...deck, verseIds: [...deck.verseIds] };
  }
  const prev = lib.decks[idx]!;
  const next: NamedDeck = {
    ...prev,
    verseIds: cleaned,
    updatedAt: Date.now(),
  };
  lib.decks[idx] = next;
  lib.activeId = next.id;
  writeLibrary(lib);
  return { ...next, verseIds: [...next.verseIds] };
}

/** Create a new named deck (optionally from verses; defaults to starter). */
export function createDeck(
  name?: string,
  verseIds?: string[],
): NamedDeck {
  loadCollection();
  const lib = ensureStoredLibrary();
  const min = deckMin();
  const cleaned =
    verseIds && verseIds.length
      ? sanitizeDeck(verseIds)
      : fillToMin(sanitizeDeck([...DEFAULT_DECK_IDS]));
  if (cleaned.length < min) {
    throw new Error(`Deck needs at least ${min} verses.`);
  }
  const deck: NamedDeck = {
    id: newId(),
    name: uniqueName(name?.trim() || "New deck", lib.decks),
    verseIds: cleaned.slice(0, deckMax()),
    updatedAt: Date.now(),
  };
  lib.decks.push(deck);
  lib.activeId = deck.id;
  writeLibrary(lib);
  return { ...deck, verseIds: [...deck.verseIds] };
}

/** Save current verses as a brand-new deck and make it active. */
export function saveDeckAs(name: string, verseIds: string[]): NamedDeck {
  const cleaned = sanitizeDeck(verseIds);
  const min = deckMin();
  if (cleaned.length < min) {
    throw new Error(`Deck needs at least ${min} verses.`);
  }
  return createDeck(name, cleaned);
}

export function renameDeck(id: string, name: string): NamedDeck | null {
  const lib = ensureStoredLibrary();
  const idx = lib.decks.findIndex((d) => d.id === id);
  if (idx < 0) return null;
  const trimmed = name.trim().slice(0, 40);
  if (!trimmed) throw new Error("Name cannot be empty.");
  const next: NamedDeck = {
    ...lib.decks[idx]!,
    name: uniqueName(trimmed, lib.decks, id),
    updatedAt: Date.now(),
  };
  lib.decks[idx] = next;
  writeLibrary(lib);
  return { ...next, verseIds: sanitizeDeck(next.verseIds) };
}

/** Delete a deck. Keeps at least one; returns the new active deck. */
export function deleteDeck(id: string): NamedDeck {
  const lib = ensureStoredLibrary();
  if (lib.decks.length <= 1) {
    throw new Error("Keep at least one deck.");
  }
  const nextDecks = lib.decks.filter((d) => d.id !== id);
  if (nextDecks.length === lib.decks.length) {
    throw new Error("Deck not found.");
  }
  const activeId =
    lib.activeId === id ? nextDecks[0]!.id : lib.activeId;
  writeLibrary({ activeId, decks: nextDecks });
  return loadDeck(activeId);
}

/**
 * Point “current loadout” at the deck a run started with.
 * Does **not** rewrite verseIds — a run pool (checkpoint, field gift, fill)
 * must never overwrite a loadout the player saved in the builder.
 */
export function markDeckUsed(deckId?: string | null): void {
  const lib = ensureStoredLibrary();
  const id =
    (deckId && lib.decks.some((d) => d.id === deckId) && deckId) ||
    lib.activeId;
  if (!id || !lib.decks.some((d) => d.id === id)) return;
  if (lib.activeId === id) return;
  lib.activeId = id;
  writeLibrary(lib);
}

/**
 * Playable pool from a preferred list (or active deck) ∩ available (owned) IDs,
 * topped up from starter / owned if needed.
 * Preferred may include duplicate ids (multiplicity); each id is capped by
 * copies owned (or 1 if present only in `availableIds`).
 */
export function resolvePlayableDeck(
  availableIds: readonly string[],
  preferredIds?: readonly string[] | null,
): string[] {
  const avail = new Set(availableIds);
  const min = effectiveDeckMin(avail.size);
  const max = effectiveDeckMax(avail.size);
  const source =
    preferredIds && preferredIds.length
      ? preferredIds
      : loadDeck().verseIds;

  const used: Record<string, number> = {};
  const preferred: string[] = [];
  for (const raw of source) {
    const id = resolveVerseId(raw);
    if (!avail.has(id)) continue;
    const owned = copiesOwned(id);
    const limit = owned > 0 ? owned : 1;
    const n = used[id] ?? 0;
    if (n >= limit) continue;
    used[id] = n + 1;
    preferred.push(id);
    if (preferred.length >= max) break;
  }
  if (preferred.length >= min) {
    return preferred.slice(0, max);
  }

  const tryFill = (id: string, filled: string[]): boolean => {
    if (!avail.has(id)) return false;
    const owned = copiesOwned(id);
    const limit = owned > 0 ? owned : 1;
    const n = used[id] ?? 0;
    if (n >= limit) return false;
    filled.push(id);
    used[id] = n + 1;
    return true;
  };

  const filled = [...preferred];
  for (const id of DEFAULT_DECK_IDS) {
    if (filled.length >= min) break;
    tryFill(id, filled);
  }
  if (filled.length < min) {
    for (const id of availableIds) {
      if (filled.length >= min) break;
      tryFill(id, filled);
    }
  }
  return filled.slice(0, max);
}

/** Resolve playable ids for a library deck id (or active if missing). */
export function resolvePlayableDeckById(
  availableIds: readonly string[],
  deckId?: string | null,
): string[] {
  const deck = loadDeck(deckId);
  return resolvePlayableDeck(availableIds, deck.verseIds);
}

export function deckSummary(verseIds: string[]): string {
  if (!verseIds.length) return "No deck saved";
  const refs = verseIds
    .map((id) => catalogById(id)?.reference)
    .filter(Boolean) as string[];
  if (refs.length <= 3) return refs.join(" · ");
  return `${refs.slice(0, 3).join(" · ")} · +${refs.length - 3} more`;
}

/** Current min/max for UI (collection-aware). */
export function currentDeckBounds(): { min: number; max: number } {
  return { min: deckMin(), max: deckMax() };
}
